'use client';
import {
  SearchAnalyticsBoundary,
  SearchAnalyticsItem,
  type SearchAnalyticsData,
} from '@eliadmin/core/src/components/analytics/search-analytics-boundary';
import { SearchSort } from '@eliadmin/core/src/components/header/search-bar/search-sort';
import { searchSortOptions } from '@eliadmin/core/src/utils/search-sort';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  buildProductsQuery,
  parseProductSearchParams,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import { Heading } from '../../components/shared/heading';
import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { ProductListSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

export default function SearchPage() {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();

  const searchParams = useSearchParams();
  const currency = useGlobalStore(s => s.generalLayout?.currency);
  const { me, isInitialized } = useCustomerStore();

  const query = buildProductsQuery({
    ...parseProductSearchParams(searchParams),
    ...searchSortOptions(searchParams),
  });
  query.results = 12;
  const productRequestUri = buildProductsRequest(query).uri;
  const productResetUri = buildProductsRequest({ ...query, page: undefined }).uri;

  const {
    data: products,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, productRequestUri, me?.customer.id ?? null],
    queryFn: () => fetchProducts({ locale, query, authToken: me?.authToken }),
    enabled: isInitialized,
  });

  return (
    <section className={cn('custom-section', 'py-12 lg:py-24')}>
      <div
        className={cn(
          'custom-container',
          'ea-manufacturer__container',
          'flex flex-col items-start justify-start gap-8 sm:gap-12',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-4 lg:gap-8 lg:pl-4">
          <Heading title={t('text211')} />
          <p className="text-sm font-light text-black/70 sm:text-base">
            {searchParams.get('search') ? `${t('text211')}: ${searchParams.get('search')}` : ''}
          </p>
        </div>
        {isLoading ? (
          <div className={cn('flex h-[300px] w-full items-center justify-center')}>
            <Spinner />
          </div>
        ) : (
          <>
            <>
              <SearchSort />
              <SearchAnalyticsBoundary
                mode="submitted"
                active={!isFetching}
                data={{
                  manifest: products?.meta?.search,
                  identity: JSON.stringify([
                    locale,
                    buildProductsRequest(query).uri,
                    me?.customer.id ?? null,
                  ]),
                  locale,
                  results: (products?.data ?? []).map(product => ({
                    type: 'product',
                    id: product.id,
                    href: product.slug,
                  })),
                  offset: products?.meta?.from
                    ? products.meta.from - 1
                    : (Number(query.page ?? 1) - 1) * Number(query.results ?? 12),
                }}
              >
                <div className="grid w-full grid-cols-2 gap-2 sm:gap-3 md:gap-4 lg:grid-cols-3 lg:gap-6">
                  {!!products?.data?.length && (
                    <ProductListSchema products={products.data} currency={currency} />
                  )}
                  {!!products?.data?.length && (
                    <TrackComponent
                      eventName={'view_item_list'}
                      eventParams={{
                        item_list_id: `search:${searchParams.get('search') ?? ''}`,
                        item_list_name: 'search',
                      }}
                      items={products.data}
                    />
                  )}
                  {products?.data.map((product, searchIndex) => (
                    <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                      {<ProductCard key={product.slug} product={product} />}
                    </SearchAnalyticsItem>
                  ))}
                </div>
              </SearchAnalyticsBoundary>
            </>
          </>
        )}
        <div className="flex w-full items-center justify-center">
          <Suspense>
            <Pagination links={products?.meta?.links ?? []} resetWhen={[productResetUri]} />
          </Suspense>
        </div>
      </div>
    </section>
  );
}
